import React, { useMemo } from 'react'
import { graphql, useFragment } from 'react-relay'

import { XStack, YStack } from 'tamagui'

import { Paragraph, Strong } from '@newComponents/Text'

import { EventTicketingTierItem_eventTicketingTier$key } from './__generated__/EventTicketingTierItem_eventTicketingTier.graphql'

export const EventTicketingTierItem = ({
  eventTicketingTier,
}: {
  eventTicketingTier: EventTicketingTierItem_eventTicketingTier$key
}) => {
  const data = useFragment(
    graphql`
      fragment EventTicketingTierItem_eventTicketingTier on EventTicketingTier {
        id
        name
        description

        currency
        price
        includeTax
        taxName
        taxPercentage
      }
    `,
    eventTicketingTier
  )

  const formattedPrice = useMemo(() => {
    const price = data.price / 100

    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: data.currency,
        maximumFractionDigits: price % 1 === 0 ? 0 : undefined,
      }).format(price)
    } catch (err) {
      return `${price} ${data.currency}`
    }
  }, [data.price, data.currency])

  const formattedTax = useMemo(() => {
    if (!data.taxName || !data.taxPercentage) {
      return 'Including tax'
    }

    if (data.includeTax) {
      return `Including ${data.taxName} @ ${data.taxPercentage * 100}%`
    }

    return `Excluding ${data.taxName} @ ${data.taxPercentage * 100}%`
  }, [data.includeTax, data.taxName, data.taxPercentage])

  return (
    <XStack gap='$4'>
      <YStack gap='$1' flex={1}>
        <Strong>{data.name}</Strong>

        <Paragraph size='$4'>{data.description}</Paragraph>
      </YStack>

      <YStack alignItems='flex-end'>
        <Strong
          size='$7'
          fontWeight='$7'
          style={{
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formattedPrice}
        </Strong>

        <Paragraph
          size='$3'
          style={{
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formattedTax}
        </Paragraph>
      </YStack>
    </XStack>
  )
}
